import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { publicTenantLogoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("admin@abrah.co.ug");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/public/tenants/abrah").then(({ data }) => setBrand(data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await login(email, password);
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F5F1]">
      {/* Left panel - form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className={`h-11 w-11 rounded-md flex items-center justify-center font-display font-extrabold text-xl overflow-hidden shrink-0 ${
              brand?.logo_file_id || !brand?.name || /abrah/i.test(brand?.name || "")
                ? "bg-white border border-gray-200" : "bg-emerald-900 text-amber-400"
            }`}>
              {brand?.logo_file_id ? (
                <img src={publicTenantLogoUrl(brand.logo_file_id)} alt={`${brand?.name || "Abrah"} logo`}
                  className="h-full w-full object-contain" data-testid="login-tenant-logo" />
              ) : !brand?.name || /abrah/i.test(brand?.name || "") ? (
                <img src="/abrah-logo.jpeg" alt="Abrah Supermarket logo"
                  className="h-full w-full object-contain" data-testid="login-default-logo" />
              ) : "A"}
            </div>
            <div>
              <div className="font-display font-bold text-lg text-gray-900 leading-tight">
                AEOIBCMS
              </div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">
                {brand?.name ? `${brand.name} Operations` : "Abrah Enterprise Operations"}
              </div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            Welcome back.
          </h1>
          <p className="text-sm text-gray-600 mt-2 mb-8 leading-relaxed">
            Sign in to manage branches, issues, assets, contacts and operations
            across Abrah Supermarket Ltd.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@abrah.co.ug"
                required
                data-testid="login-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="mt-1.5 h-11"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-11 bg-emerald-900 hover:bg-emerald-950 text-white font-semibold"
            >
              {busy ? "Signing in…" : "Sign in to console"}
            </Button>

            <div className="relative flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-[10px] uppercase tracking-widest text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
            <GoogleSignInButton intent="staff" label="Continue with Google" />
            <div className="text-[11px] text-gray-500 text-center">
              Only pre-invited staff emails can sign in with Google.
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 pt-2">
              <ShieldCheck size={14} className="text-emerald-800" />
              Secured with role-based access · 6 branches · 50+ supported
            </div>
            <div className="text-center pt-3 border-t border-gray-100 mt-3 space-x-3 text-xs">
              <a href="/site" className="text-emerald-900 hover:underline font-semibold" data-testid="login-public-site-link">Visit public website →</a>
              <span className="text-gray-300">|</span>
              <a href="/signup" className="text-amber-700 hover:underline font-semibold" data-testid="login-signup-link">Open your own supermarket account</a>
            </div>
          </form>
        </div>
      </div>

      {/* Right panel - hero */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Corporate building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/85 via-emerald-900/75 to-amber-900/60" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300/90">
            <span className="h-px w-8 bg-amber-300/60" />
            Uganda · Multi-branch retail
          </div>
          <div className="max-w-md">
            <p className="font-display text-3xl xl:text-4xl font-semibold leading-tight">
              &ldquo;Centralized operations across every branch — issues, assets,
              suppliers, and strategic contacts in one console.&rdquo;
            </p>
            <div className="mt-6 grid grid-cols-3 gap-6 text-amber-50/90">
              <div>
                <div className="font-display text-2xl font-bold text-amber-300">6</div>
                <div className="text-[10px] uppercase tracking-widest mt-1">Active branches</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-amber-300">13</div>
                <div className="text-[10px] uppercase tracking-widest mt-1">Modules</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-amber-300">∞</div>
                <div className="text-[10px] uppercase tracking-widest mt-1">Records</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
